import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import { SEO } from '../components/SEO';

export const MarketplaceTermsPage = () => {
  const navigate = useNavigate();
  const effectiveDate = 'April 12, 2026';

  return (
    <div className="min-h-screen bg-background" data-testid="marketplace-terms-page">
      <SEO
        title="Marketplace Terms of Use"
        description="Terms governing the TurfPulse Community Marketplace — a user-to-user bulletin board for group buys, with strict FIFRA-compliant product category restrictions."
        path="/marketplace-terms"
      />      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-4xl mx-auto flex items-center gap-3 px-4 sm:px-6 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </Button>
          <TurfPulseLogoSVG size={28} />
          <span className="font-semibold text-base tracking-tight">Marketplace Terms of Use</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">TurfPulse Community Marketplace — Terms of Use</h1>
        <p className="text-sm text-muted-foreground mb-8">Effective Date: {effectiveDate}</p>

        <div className="prose prose-sm dark:prose-invert max-w-none space-y-6 text-foreground/85 leading-relaxed">

          <section>
            <h2 className="text-lg font-bold text-foreground mt-8 mb-3">1. Overview</h2>
            <p>
              The TurfPulse Community Marketplace ("Marketplace") is a community bulletin board feature within the TurfPulse platform that allows registered users to post, browse, and coordinate group purchases of lawn care products and related supplies. TurfPulse provides the Marketplace solely as a communication tool to connect users with shared purchasing interests.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mt-8 mb-3">2. TurfPulse's Role — Bulletin Board Only</h2>
            <p>
              <strong>TurfPulse is not a party to any transaction conducted through the Marketplace.</strong> TurfPulse does not:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Facilitate, process, or guarantee any payments between users</li>
              <li>Handle, ship, store, or distribute any products</li>
              <li>Verify the identity, credentials, or reliability of any user</li>
              <li>Inspect, certify, or guarantee the quality, safety, legality, or condition of any product listed</li>
              <li>Mediate or resolve disputes between users</li>
              <li>Endorse, recommend, or approve any listing or transaction</li>
            </ul>
            <p className="mt-3">
              All transactions, including but not limited to payment, shipping, handling, pickup, splitting, and distribution of products, are conducted entirely between users at their own discretion and risk.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mt-8 mb-3">3. User Responsibilities</h2>
            <p>By using the Marketplace, you agree that:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li><strong>Accuracy:</strong> You will provide truthful and accurate information in all listings and communications</li>
              <li><strong>Payments:</strong> All payments are arranged directly between participants using methods of their choosing (e.g., Venmo, Zelle, cash). TurfPulse does not collect, hold, or transfer funds</li>
              <li><strong>Shipping & Logistics:</strong> All shipping costs, arrangements, logistics, and delivery coordination are the sole responsibility of the participants involved</li>
              <li><strong>Communication:</strong> You will communicate respectfully and in good faith with other participants</li>
              <li><strong>Completion:</strong> Organizers are responsible for closing listings once a group buy is completed or no longer active</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mt-8 mb-3">4. Prohibited Product Categories</h2>
            <p>
              <strong>The following product categories are prohibited from the Marketplace</strong> due to federal pesticide regulations under FIFRA (Federal Insecticide, Fungicide, and Rodenticide Act) and corresponding state-level pesticide laws that regulate the sale, distribution, and transport of chemical products in all 50 states:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Plant Growth Regulators (PGR)</li>
              <li>Pre-Emergent Herbicides</li>
              <li>Post-Emergent Herbicides</li>
              <li>Fungicides</li>
              <li>Insecticides</li>
            </ul>
            <p className="mt-3">
              <strong>Permitted categories include:</strong> Fertilizer, Soil Amendment, Seed, Equipment, Sprayer Parts, Mower Parts, and Other non-regulated products. TurfPulse enforces this restriction at the platform level and reserves the right to remove any listing that violates this policy.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mt-8 mb-3">5. Additional Regulatory Compliance</h2>
            <p>
              <strong>Certain lawn care products are regulated at the federal, state, and local level.</strong> This includes but is not limited to pesticides, herbicides, fungicides, plant growth regulators, and other chemical products. Regulations vary significantly by jurisdiction. For example:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>The State of Hawaii restricts the import, sale, and use of specific pesticides and agricultural chemicals</li>
              <li>The U.S. Environmental Protection Agency (EPA) regulates pesticide distribution under FIFRA (Federal Insecticide, Fungicide, and Rodenticide Act)</li>
              <li>Many states require permits or licenses for the purchase, transport, or application of restricted-use products</li>
            </ul>
            <p className="mt-3">
              <strong>It is YOUR sole responsibility</strong> to verify that any product you list, purchase, transport, or use through the Marketplace complies with all applicable federal, state, and local laws and regulations. TurfPulse makes no representations regarding the legality of any product in any jurisdiction. Violation of applicable laws is solely the responsibility of the user.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mt-8 mb-3">5. Assumption of Risk</h2>
            <p>
              By participating in the Marketplace, you expressly acknowledge and accept that:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>You are engaging in transactions with other users at your own risk</li>
              <li>TurfPulse has no control over and does not guarantee the quality, safety, legality, or availability of any product listed</li>
              <li>TurfPulse has no control over and does not guarantee the ability of any user to complete a transaction</li>
              <li>There are inherent risks in purchasing products from individuals, including but not limited to misrepresentation, non-delivery, product defects, and financial loss</li>
              <li>You assume full responsibility for evaluating the risks of any transaction</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mt-8 mb-3">6. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, TURFPULSE, ITS OWNERS, OPERATORS, AFFILIATES, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Any transaction or attempted transaction between users</li>
              <li>Any product listed, sold, purchased, shipped, or used through the Marketplace</li>
              <li>Any dispute between users</li>
              <li>Any loss of funds, products, or property</li>
              <li>Any injury, illness, or damage resulting from the use of any product</li>
              <li>Any violation of applicable laws or regulations by any user</li>
              <li>Any failure of any user to fulfill their obligations in a transaction</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mt-8 mb-3">7. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless TurfPulse, its owners, operators, affiliates, employees, and agents from and against any and all claims, liabilities, damages, losses, costs, and expenses (including reasonable attorneys' fees) arising out of or related to your use of the Marketplace, any transaction you participate in, any product you list or purchase, or any violation of these Terms or applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mt-8 mb-3">8. Listing Policies</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Listings expire after 30 days and may be renewed by the organizer</li>
              <li>TurfPulse reserves the right to remove any listing at any time for any reason, including but not limited to violations of these Terms, applicable law, or community standards</li>
              <li>Prohibited listings include: illegal products, controlled substances, firearms, stolen goods, and any product that violates applicable law</li>
              <li>TurfPulse is not obligated to monitor listings but may do so at its discretion</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mt-8 mb-3">9. Dispute Resolution</h2>
            <p>
              Any disputes between users arising from Marketplace transactions are to be resolved directly between the parties involved. TurfPulse will not mediate, arbitrate, or otherwise participate in the resolution of any user dispute. If you are unable to resolve a dispute, you may seek resolution through applicable legal channels at your own expense.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mt-8 mb-3">10. Modifications</h2>
            <p>
              TurfPulse reserves the right to modify these Marketplace Terms of Use at any time. Continued use of the Marketplace following any changes constitutes acceptance of the modified Terms. Users will be notified of material changes through the platform.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mt-8 mb-3">11. Contact</h2>
            <p>
              For questions regarding these Terms, contact us at{' '}
              <a href="mailto:support@getturfpulse.com" className="text-primary hover:underline">support@getturfpulse.com</a>.
            </p>
          </section>

          <section className="mt-10 pt-6 border-t border-border">
            <p className="text-xs text-muted-foreground italic">
              These Marketplace Terms of Use are supplemental to the TurfPulse <a href="/terms" className="text-primary hover:underline">Terms of Service</a> and <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>. In the event of a conflict, these Marketplace Terms shall govern with respect to Marketplace-related activity.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};
